<template>
  <div class="content">
    <base-header class="pb-6">
      <div class="row align-items-center py-4">
        <div class="col-lg-6 col-7">
          <h6 class="h2 text-white d-inline-block mb-0">
            {{ $t("kol.menu.product_sends") }}
          </h6>
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-bread-crumb></route-bread-crumb>
          </nav>
        </div>
        <div class="col-lg-6 col-5 text-right">
          <base-button size="md" type="neutral" @click="handleCreate()">
            <i class="fa fa-plus"></i> {{ $t("add") }}
          </base-button>
        </div>
      </div>
    </base-header>
    <div class="container-fluid mt--6">
      <div>
        <card class="no-border-card" body-classes="px-0 pb-1" footer-classes="pb-2">
          <template slot="header">
            <div class="row">
              <div class="col-md-4">
                <el-date-picker
                  v-model="dateRange"
                  type="daterange"
                  range-separator="-"
                  :start-placeholder="$t('date_from')"
                  :end-placeholder="$t('date_to')"
                  format="yyyy-MM-dd"
                  value-format="yyyy-MM-dd"
                  size="small"
                  @change="fetchData"
                ></el-date-picker>
              </div>
            </div>
          </template>
          <div>
            <el-table
              :data="items"
              row-key="id"
              header-row-class-name="thead-light"
              v-loading="loading"
            >
              <el-table-column :label="$t('date')" min-width="120">
                <template v-slot="{ row }">{{ formatDate(row.order_date || row.created_at) }}</template>
              </el-table-column>
              <el-table-column prop="staff_name" :label="$t('kol.staff_name')" min-width="140"></el-table-column>
              <el-table-column prop="creator_name" :label="$t('kol.creator_name')" min-width="150"></el-table-column>
              <el-table-column prop="tracking_number" :label="$t('tracking_number')" min-width="140"></el-table-column>
              <el-table-column prop="recipient_name" :label="$t('name')" min-width="140"></el-table-column>
              <el-table-column prop="order_status" :label="$t('order_status')" min-width="100" align="center">
                <template v-slot="{ row }">
                  <span v-if="row.order_status !== null && row.order_status !== undefined">{{ row.order_status }}</span>
                  <span v-else>-</span>
                </template>
              </el-table-column>
              <el-table-column :label="$t('amount')" min-width="110" align="right">
                <template v-slot="{ row }">{{ row.order_amount ? row.order_amount.toLocaleString() : '-' }}</template>
              </el-table-column>
              <el-table-column prop="note" :label="$t('note')" min-width="150"></el-table-column>
              <el-table-column :label="$t('action')" min-width="100" align="center">
                <template v-slot="{ row }">
                  <base-button size="sm" type="danger" @click="handleDelete(row)">
                    <i class="fa fa-trash"></i>
                  </base-button>
                </template>
              </el-table-column>
            </el-table>
          </div>
          <div slot="footer" class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap">
            <div class="">
              <p class="card-category">
                {{ $t("data") }} {{ (currentPage - 1) * perPage + 1 }} {{ $t("to") }} {{ Math.min(currentPage * perPage, total) }}
                {{ $t("of") }} {{ total }}
              </p>
            </div>
            <div class="col-12 text-center mt-2">
              <el-pagination
                background
                layout="prev, pager, next"
                :total="total"
                :page-size="perPage"
                :current-page="currentPage"
                @current-change="handlePageChange"
              ></el-pagination>
            </div>
          </div>
        </card>
      </div>
    </div>

    <!-- Create Dialog -->
    <el-dialog :title="$t('kol.menu.product_sends')" :visible.sync="showCreateDialog" width="520px">
      <el-form :model="form" label-width="140px" size="small">
        <el-form-item :label="$t('kol.creator_name')">
          <el-select v-model="form.creator_id" filterable :placeholder="$t('kol.select_creator')" style="width:100%">
            <el-option v-for="c in creators" :key="c.id" :label="c.name" :value="c.id"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item :label="$t('tracking_number')">
          <el-input v-model="form.tracking_number"></el-input>
        </el-form-item>
        <el-form-item :label="$t('note')">
          <el-input v-model="form.note" type="textarea" :rows="2"></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <base-button type="secondary" @click="showCreateDialog = false">{{ $t("cancel") }}</base-button>
        <base-button type="primary" :loading="saving" @click="submitCreate()">{{ $t("save") }}</base-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import {
  Table, TableColumn, DatePicker, Dialog, Form, FormItem,
  Select, Option, Input, Pagination,
} from "element-ui";
import KOLProvider from "@/resource/kol";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import swal from "sweetalert2";

const kol = new KOLProvider();

export default {
  name: "KOLProductSendTables",
  components: {
    RouteBreadCrumb,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [DatePicker.name]: DatePicker,
    [Dialog.name]: Dialog,
    [Form.name]: Form,
    [FormItem.name]: FormItem,
    [Select.name]: Select,
    [Option.name]: Option,
    [Input.name]: Input,
    [Pagination.name]: Pagination,
  },
  data() {
    return {
      loading: false,
      saving: false,
      items: [],
      total: 0,
      currentPage: 1,
      perPage: 50,
      dateRange: null,
      creators: [],
      showCreateDialog: false,
      form: {
        creator_id: null,
        tracking_number: "",
        note: "",
      },
    };
  },
  mounted() {
    this.fetchData();
    this.fetchCreators();
  },
  methods: {
    async fetchData() {
      this.loading = true;
      try {
        const params = { page: this.currentPage, per_page: this.perPage };
        if (this.dateRange && this.dateRange.length === 2) {
          params.date_from = this.dateRange[0];
          params.date_to = this.dateRange[1];
        }
        const res = await kol.ProductSendList(params);
        if (res.data && res.data.Data) {
          this.items = res.data.Data[0] ? res.data.Data[0].items || [] : [];
          this.total = res.data.Data[0] ? res.data.Data[0].total || 0 : 0;
        }
      } catch (err) {
        console.log(err);
      }
      this.loading = false;
    },
    async fetchCreators() {
      try {
        const res = await kol.CreatorList({ is_active: "true", per_page: 200 });
        if (res.data && res.data.Data) {
          this.creators = res.data.Data[0] ? res.data.Data[0].items || [] : [];
        }
      } catch (err) {
        console.log(err);
      }
    },
    handlePageChange(page) {
      this.currentPage = page;
      this.fetchData();
    },
    handleCreate() {
      this.form = { creator_id: null, tracking_number: "", note: "" };
      this.showCreateDialog = true;
    },
    async submitCreate() {
      if (!this.form.creator_id || !this.form.tracking_number) {
        swal.fire({ icon: "warning", title: this.$t("warning"), text: this.$t("please_fill_required"), confirmButtonClass: "btn btn-warning btn-fill", buttonsStyling: false });
        return;
      }
      this.saving = true;
      try {
        await kol.ProductSendCreate({
          creator_id: this.form.creator_id,
          tracking_number: this.form.tracking_number,
          note: this.form.note,
        });
        this.showCreateDialog = false;
        this.fetchData();
      } catch (err) {
        console.log(err);
      }
      this.saving = false;
    },
    async handleDelete(row) {
      const result = await swal.fire({ icon: "warning", title: this.$t("confirm_delete"), showCancelButton: true, confirmButtonClass: "btn btn-danger btn-fill", cancelButtonClass: "btn btn-secondary btn-fill", buttonsStyling: false });
      if (!result.value) return;
      try {
        await kol.ProductSendDelete(row.id);
        this.fetchData();
      } catch (err) {
        console.log(err);
      }
    },
    formatDate(d) {
      if (!d) return "-";
      return String(d).substring(0, 10);
    },
  },
};
</script>
